<script setup lang="ts">
	import { type ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api";
	import startPointerInvalidSvg from "@/assets/start-pointer-invalid.svg?raw";
	import startPointerSvg from "@/assets/start-pointer.svg?raw";
	import warningTriangleSvg from "@/assets/warning-triangle.svg?raw";
	import {
		DubinsSteeringStateSpace,
		HEADLANDS_MAX_DEPTH,
		HEADLANDS_MIN_DEPTH,
		PASS_ORDER_VARIANTS,
		UNCAP_HEADLANDS_MAX_DEPTH,
		UNCAP_HEADLANDS_MIN_DEPTH,
		type ImplementWithCalibration,
		type PlanPathResult,
		type SingleMachineField,
	} from "@shared/types";
	import type { Position } from "geojson";
	import {
		computed,
		inject,
		onUnmounted,
		reactive,
		ref,
		type Ref,
		watch,
	} from "vue";
	import {
		getImplementSwathExtension,
		optionsFromImplementModel,
		planPath,
	} from "@shared/composables";
	import { feetAndInchesToMeters } from "@shared/utils";
	import { useHttp } from "@ptx-autonomous/libs/api";
	import { useValidation } from "@ptx-autonomous/libs/forms";
	import { z } from "zod";
	import { centerOfMass as turfCenterOfMass } from "@turf/center-of-mass";
	import {
		abLineSelectedInnerStroke,
		abLineSelectedOuterStroke,
		abLineUnselectedInnerStroke,
		abLineUnselectedOuterStroke,
		DEFAULT_TARGET_SPEED,
		DEFAULT_TURNING_SPEED,
		DEFAULT_APPROACH_SPEED,
		MIN_MACHINE_SPEED,
		MAX_MACHINE_SPEED,
	} from "@shared/state";
	import { Distance } from "@ptx-autonomous/libs/units";
	import { CancellationToken } from "@shared/utils";
	import { LngLat } from "maplibre-gl";
	import {
		MapLibreLabel,
		MapLibreMarker,
		MapLibrePolyline,
		type Marker,
		useMap,
	} from "@ptx-autonomous/libs/maplibre";
	import { lineString } from "@turf/helpers";
	import nearestPointOnLine from "@turf/nearest-point-on-line";
	import type { MapMouseEvent } from "maplibre-gl";
	import {
		AB_LINE_ANGLE_TOLERANCE_DEGREES,
		AB_LINE_MIN_LENGTH_METERS,
		AB_LINE_SELECTION_MAX_DISTANCE_METERS,
		extractStraightSegments,
		type FieldSegment,
	} from "@shared/utils";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import ChevronLeftIcon from "@/icons/ChevronLeftIcon.vue";
	import CloseButtonAlt from "@/icons/CloseButtonAlt.vue";
	import MapIcon from "@/icons/MapIcon.vue";
	import ButtonBack from "../ButtonBack.vue";
	import ButtonPrimary from "../ButtonPrimary.vue";
	import ButtonSecondary from "../ButtonSecondary.vue";

	const tractorTurningRadius = inject<Ref<{ feet: number; inches: number }>>(
		"tractorTurningRadius",
	)!;
	const tractorImplementTurningRadius = inject(
		"tractorImplementTurningRadius",
	) as Ref<{ feet: number; inches: number }>;
	const rearAxleToHitch = inject("rearAxleToHitch") as Ref<{
		feet: number;
		inches: number;
	}>;

	const props = defineProps<{
		selectedField: SingleMachineField;
		selectedImplement: ImplementV2Model;
		planningOptions: {
			heading: number;
			overlap: number;
			prefersConsecutiveOrder: boolean;
			startLocation: Position | undefined;
			targetSpeed: number;
			turningSpeed: number;
			approachSpeed: number;
			uncapHeadlands: boolean;
		};
	}>();

	const emits = defineEmits<{
		resetOmnibar: [void];
		backToSelectField: [void];
		selectingStartLocation: [selecting: boolean];
		selectingAbLine: [selecting: boolean];
		pathsGenerated: [pathResults: PlanPathResult[]];
		optionsUpdate: [options: typeof props.planningOptions];
	}>();

	onUnmounted(() => {
		if (cancellationToken.value) {
			cancellationToken.value.cancel();
		}
		isPlanning.value = false;

		startLocationMarker.value = undefined;
		cancellationToken.value = undefined;
		pathResults.value = undefined;
		hasError.value = false;
		map.off("click", handleAbLineMapClick);
	});

	const http = useHttp();
	const isPlanning = ref(false);
	const planningProgress = ref(0);
	const hasError = ref(false);
	const totalPathOptions = computed(() => {
		const minDepth = props.planningOptions.uncapHeadlands
			? UNCAP_HEADLANDS_MIN_DEPTH
			: HEADLANDS_MIN_DEPTH;
		const maxDepth = props.planningOptions.uncapHeadlands
			? UNCAP_HEADLANDS_MAX_DEPTH
			: HEADLANDS_MAX_DEPTH;

		const depthOptions = Math.ceil(maxDepth - minDepth) + 1;

		if (formData.prefersConsecutiveOrder) {
			return depthOptions;
		}

		return depthOptions * PASS_ORDER_VARIANTS;
	});

	const isSelectingStartLocation = ref(false);
	const cancellationToken = ref<CancellationToken>();
	const pathResults = ref<PlanPathResult[]>();

	const { map } = useMap();
	const isSelectingAbLine = ref(false);
	const selectedSegment = ref<FieldSegment | null>(null);

	const straightSegments = computed(() =>
		extractStraightSegments(props.selectedField.fieldBoundary, {
			angleTolerance: AB_LINE_ANGLE_TOLERANCE_DEGREES,
			minLengthMeters: AB_LINE_MIN_LENGTH_METERS,
		}),
	);
	const unselectedStraightSegments = computed(() =>
		straightSegments.value.filter(seg => seg !== selectedSegment.value),
	);

	function segmentLabelLocation(segment: FieldSegment): Position {
		return [
			(segment.startPoint[0] + segment.endPoint[0]) / 2,
			(segment.startPoint[1] + segment.endPoint[1]) / 2,
		];
	}

	function startAbLineSelection() {
		isSelectingAbLine.value = true;
		selectedSegment.value = null;
		emits("selectingAbLine", true);
	}

	function cancelAbLineSelection() {
		isSelectingAbLine.value = false;
		selectedSegment.value = null;
		emits("selectingAbLine", false);
	}

	function confirmAbLineSelection() {
		if (!selectedSegment.value) return;
		formData.heading = selectedSegment.value.heading;
		validation.touch.heading();
		cancelAbLineSelection();
	}

	function handleAbLineMapClick(event: MapMouseEvent) {
		const clickPt = {
			type: "Feature" as const,
			geometry: {
				type: "Point" as const,
				coordinates: [event.lngLat.lng, event.lngLat.lat],
			},
			properties: {},
		};
		let closest: FieldSegment | null = null;
		let closestDist = Infinity;

		for (const seg of straightSegments.value) {
			const line = lineString([seg.startPoint, seg.endPoint]);
			const nearest = nearestPointOnLine(line, clickPt, {
				units: "meters",
			});
			const dist = nearest.properties.dist ?? Infinity;
			if (dist < closestDist) {
				closestDist = dist;
				closest = seg;
			}
		}

		if (closest && closestDist < AB_LINE_SELECTION_MAX_DISTANCE_METERS) {
			selectedSegment.value = closest;
		}
	}

	watch(isSelectingAbLine, active => {
		if (active) map.on("click", handleAbLineMapClick);
		else map.off("click", handleAbLineMapClick);
	});

	const pathPlanningOptions = computed({
		get: () => props.planningOptions,
		set: value => emits("optionsUpdate", value),
	});

	const swathExtensionFromImplement = computed(() => {
		const maxMeters = getImplementSwathExtension(
			props.selectedImplement,
			feetAndInchesToMeters(
				rearAxleToHitch.value.feet,
				rearAxleToHitch.value.inches,
			),
		);
		const options = optionsFromImplementModel(props.selectedImplement);
		const percentage = options?.swathExtensionPercentage ?? 0;
		return maxMeters * (percentage / 100);
	});

	// Create reactive form data for validation
	const formData = reactive({
		heading: 0,
		overlap: 0,
		prefersConsecutiveOrder: false,
		targetSpeed: DEFAULT_TARGET_SPEED,
		turningSpeed: DEFAULT_TURNING_SPEED,
		approachSpeed: DEFAULT_APPROACH_SPEED,
	});

	// Sync formData with props
	watch(
		() => props.planningOptions,
		newOptions => {
			Object.assign(formData, {
				heading: newOptions.heading,
				overlap: newOptions.overlap,
				prefersConsecutiveOrder: newOptions.prefersConsecutiveOrder,
				targetSpeed: newOptions.targetSpeed,
				turningSpeed: newOptions.turningSpeed,
				approachSpeed: newOptions.approachSpeed,
			});
		},
		{ immediate: true },
	);

	// Sync changes back to parent
	watch(formData, newFormData => {
		emits("optionsUpdate", {
			...props.planningOptions,
			...newFormData,
		});
		// Trigger validation on change for immediate visual feedback
		validation.validate();
	});

	// Validation schema
	const validation = useValidation(
		formData,
		z.object({
			heading: z.coerce
				.number()
				.min(0, "Heading must be 0-360 degrees")
				.max(360, "Heading must be 0-360 degrees"),
			overlap: z.coerce.number().min(0, "Overlap must be at least 0"),
			prefersConsecutiveOrder: z.boolean(),
			targetSpeed: z.coerce
				.number()
				.min(1, "Target speed must be at least 1 mph")
				.gte(
					MIN_MACHINE_SPEED,
					`Target speed must be at least ${MIN_MACHINE_SPEED} mph`,
				)
				.lte(
					MAX_MACHINE_SPEED,
					`Target speed must not exceed ${MAX_MACHINE_SPEED} mph`,
				),
			turningSpeed: z.coerce
				.number()
				.min(1, "Turning speed must be at least 1 mph")
				.gte(
					MIN_MACHINE_SPEED,
					`Turning speed must be at least ${MIN_MACHINE_SPEED} mph`,
				)
				.lte(
					MAX_MACHINE_SPEED,
					`Turning speed must not exceed ${MAX_MACHINE_SPEED} mph`,
				),
			approachSpeed: z.coerce
				.number()
				.min(1, "Approach speed must be at least 1 mph")
				.gte(
					MIN_MACHINE_SPEED,
					`Approach speed must be at least ${MIN_MACHINE_SPEED} mph`,
				)
				.lte(
					MAX_MACHINE_SPEED,
					`Approach speed must not exceed ${MAX_MACHINE_SPEED} mph`,
				),
		}),
	);

	const startLocationMarker = ref<Marker>();
	const startLocation = ref<LngLat>();
	const isStartingLocationValid = ref(false);

	watch(startLocation, () => {
		if (!startLocation.value || !props.selectedField.fieldBoundary) {
			isStartingLocationValid.value = false;
			return;
		}

		const point = [startLocation.value.lng, startLocation.value.lat];
		const polygon = props.selectedField.fieldBoundary;

		isStartingLocationValid.value = booleanPointInPolygon(point, polygon);
	});

	const progressPercentage = computed(() => {
		return Math.min(
			100,
			(planningProgress.value / totalPathOptions.value) * 100,
		);
	});

	async function handlePathPlanning() {
		if (!props.selectedField || !props.selectedImplement) {
			alert("Please select both a field and implement first");
			return;
		}

		// Validate form data
		if (!validation.validate()) {
			console.error("Validation failed", validation.errors);
			return;
		}

		isPlanning.value = true;
		hasError.value = false;

		try {
			const targetSpeed = formData.targetSpeed;
			const turningSpeed = formData.turningSpeed;
			const approachSpeed = formData.approachSpeed;

			const overlapMeters = new Distance(formData.overlap, "in").m();

			const pathOptions = {
				heading: formData.heading,
				overlap: overlapMeters,
				swathExtension: swathExtensionFromImplement.value,
				prefersConsecutiveOrder: formData.prefersConsecutiveOrder,
				uncapHeadlands: true,
				targetSpeed: targetSpeed,
				turningSpeed: turningSpeed,
				approachSpeed: approachSpeed,
				startLocation: pathPlanningOptions.value.startLocation,
				done: () => {},
			};

			cancellationToken.value = new CancellationToken();

			const implementWithCalibration: ImplementWithCalibration = {
				...props.selectedImplement,
				// There is no real machine for missions planned on the web-planner so we need to make this fake calibration object to pass into the planPath function
				calibration: {
					Id: "",
					MachineId: "",
					ImplementId: props.selectedImplement.ImplementId,
					CreatedAt: new Date().toISOString(),
					UpdatedAt: new Date().toISOString(),
					TurningRadiusMeters: feetAndInchesToMeters(
						tractorTurningRadius.value.feet,
						tractorTurningRadius.value.inches,
					),
				},
			};

			pathResults.value = await planPath(
				http,
				props.selectedField.fieldBoundary,
				pathOptions,
				implementWithCalibration,
				pathPlanningOptions.value.startLocation,
				targetSpeed,
				turningSpeed,
				cancellationToken.value,
				() => {
					planningProgress.value++;
				},
				DubinsSteeringStateSpace,
				feetAndInchesToMeters(
					tractorImplementTurningRadius.value.feet,
					tractorImplementTurningRadius.value.inches,
				),
			);

			if (pathResults.value && pathResults.value.length > 0) {
				emits("pathsGenerated", pathResults.value);
			} else {
				hasError.value = true;
			}
		} catch (error) {
			if (
				error &&
				typeof error === "object" &&
				"name" in error &&
				error.name === "AbortError"
			) {
				// Planning was cancelled, emit any successful results
				if (pathResults.value && pathResults.value.length > 0) {
					emits("pathsGenerated", pathResults.value);
				}
				return;
			}
			console.error("Path planning error:", error);
			hasError.value = true;
		} finally {
			isPlanning.value = false;
			planningProgress.value = 0;
		}
	}

	function updateStartLocation(newLocation: LngLat) {
		startLocation.value = newLocation;

		if (startLocationMarker.value) {
			startLocationMarker.value.location = [
				newLocation.lng,
				newLocation.lat,
			];
		}
	}

	function skipPlanning() {
		if (cancellationToken.value) {
			cancellationToken.value.cancel();
		}
		http.abortRequest("path-planner");
	}

	const canPlanPath = computed(() => {
		const isFormValid = validation.validate();
		return (
			props.selectedField &&
			props.selectedImplement &&
			!isPlanning.value &&
			isFormValid
		);
	});

	function startLocationSelection() {
		if (pathPlanningOptions.value.startLocation) {
			pathPlanningOptions.value.startLocation = undefined;
			return;
		}

		isSelectingStartLocation.value = true;
		emits("selectingStartLocation", true);

		if (props.selectedField.fieldBoundary) {
			const fieldFeature = {
				type: "Feature",
				geometry: props.selectedField.fieldBoundary,
				properties: {},
			};

			const center = turfCenterOfMass(fieldFeature);
			startLocationMarker.value = {
				location: center.geometry.coordinates as Position,
				icon: startPointerSvg,
				draggable: true,
			};
			startLocation.value = new LngLat(
				center.geometry.coordinates[0],
				center.geometry.coordinates[1],
			);
		}
	}

	function selectStartLocation() {
		isSelectingStartLocation.value = false;
		emits("selectingStartLocation", false);
		pathPlanningOptions.value.startLocation = [
			startLocation.value!.lng,
			startLocation.value!.lat,
		];
	}

	function cancelStartLocationSelection() {
		isSelectingStartLocation.value = false;
		emits("selectingStartLocation", false);
	}

	function resetPlanningOptions() {
		// Reset all planning options to defaults
		Object.assign(formData, {
			heading: 0,
			overlap: 0, // in inches
			prefersConsecutiveOrder: false,
			targetSpeed: DEFAULT_TARGET_SPEED,
			turningSpeed: DEFAULT_TURNING_SPEED,
			approachSpeed: DEFAULT_APPROACH_SPEED,
		});

		// Reset startLocation separately since it's not in formData
		pathPlanningOptions.value = {
			...props.planningOptions,
			startLocation: undefined as Position | undefined,
		};

		// Reset implement-specific options if available
		const implementOptions = optionsFromImplementModel(
			props.selectedImplement,
		);
		if (implementOptions) {
			formData.overlap = implementOptions.overlap; // Already in inches
		}

		// Clear any other state
		startLocationMarker.value = undefined;
		startLocation.value = undefined;
		isStartingLocationValid.value = false;
		isPlanning.value = false;
		hasError.value = false;
		pathResults.value = undefined;
		planningProgress.value = 0;

		if (cancellationToken.value) {
			cancellationToken.value.cancel();
			cancellationToken.value = undefined;
		}
	}

	defineExpose({
		resetPlanningOptions,
	});
</script>

<template>
	<template v-if="!isSelectingStartLocation && !isSelectingAbLine">
		<div
			class="flex items-center justify-between p-4 bg-green-50 border-b border-green-200"
		>
			<div class="flex items-center gap-3">
				<button
					@click="emits('backToSelectField')"
					class="text-grey-400 hover:text-grey-600 p-1"
				>
					<ChevronLeftIcon class="w-5 h-5" />
				</button>
				<div>
					<h3 class="title text-green-950 text-lg font-semibold">
						Path Planning Options
					</h3>
					<div class="text-sm text-grey-600">
						{{ selectedField?.fieldName }} •
						{{ selectedImplement?.ImplementName }}
					</div>
				</div>
			</div>
			<button
				@click="emits('resetOmnibar')"
				class="text-grey-400 hover:text-grey-600"
			>
				<CloseButtonAlt class="w-5 h-5" />
			</button>
		</div>

		<template v-if="!isPlanning && !hasError">
			<div
				class="gap-2 grid grid-cols-[1.5fr_1fr_min-content] items-center justify-items-start p-4"
			>
				<span class="text-sm text-grey-750 uppercase font-semibold">
					Heading
				</span>
				<div class="flex flex-col">
					<div class="flex items-center">
						<input
							v-model.number="formData.heading"
							type="number"
							min="0"
							max="360"
							class="w-20 px-2 py-1 border rounded text-center"
							:class="{
								'border-red-600': validation.errors.heading,
								'border-grey-300': !validation.errors.heading,
							}"
							@input="validation.touch.heading()"
							@blur="validation.touch.heading()"
						/>
						<span class="ml-1 text-sm text-grey-600">°</span>
					</div>
					<div
						v-if="validation.errors.heading"
						class="text-xs text-red-600 mt-1"
					>
						{{ validation.errors.heading }}
					</div>
				</div>

				<button
					class="px-2 py-1 text-sm font-semibold border rounded border-grey-300 hover:bg-grey-100 whitespace-nowrap h-full"
					@click="startAbLineSelection"
				>
					From Field
				</button>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<span class="text-sm text-grey-750 uppercase font-semibold">
					Overlap
				</span>
				<div class="flex flex-col">
					<div class="flex items-center">
						<input
							v-model.number="formData.overlap"
							type="number"
							min="0"
							class="w-20 px-2 py-1 border rounded text-center"
							:class="{
								'border-red-600': validation.errors.overlap,
								'border-grey-300': !validation.errors.overlap,
							}"
							@input="validation.touch.overlap()"
							@blur="validation.touch.overlap()"
						/>
						<span class="ml-1 text-sm text-grey-600">in</span>
					</div>
					<div
						v-if="validation.errors.overlap"
						class="text-xs text-red-600 mt-1"
					>
						{{ validation.errors.overlap }}
					</div>
				</div>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<span
					class="text-sm py-2 text-grey-750 uppercase font-semibold text-left"
				>
					Consecutive passes
				</span>
				<div class="flex items-center">
					<input
						v-model="formData.prefersConsecutiveOrder"
						type="checkbox"
						class="w-5 h-5 accent-green-600 focus:ring-green-600 focus:ring-2 focus:ring-offset-2"
					/>
				</div>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<!-- Speed Settings Section -->
				<span class="text-sm text-grey-750 uppercase font-semibold">
					Target Speed
				</span>
				<div class="flex flex-col">
					<div class="flex items-center">
						<input
							v-model.number="formData.targetSpeed"
							type="number"
							min="0"
							step="0.1"
							class="w-20 px-2 py-1 border rounded text-center"
							:class="{
								'border-red-600': validation.errors.targetSpeed,
								'border-grey-300':
									!validation.errors.targetSpeed,
							}"
							@input="validation.touch.targetSpeed()"
							@blur="validation.touch.targetSpeed()"
						/>
						<span class="ml-1 text-sm text-grey-600">mph</span>
					</div>
					<div
						v-if="validation.errors.targetSpeed"
						class="text-xs text-red-600 mt-1"
					>
						{{ validation.errors.targetSpeed }}
					</div>
				</div>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<span class="text-sm text-grey-750 uppercase font-semibold">
					Turning Speed
				</span>
				<div class="flex flex-col">
					<div class="flex items-center">
						<input
							v-model.number="formData.turningSpeed"
							type="number"
							min="0"
							step="0.1"
							class="w-20 px-2 py-1 border rounded text-center"
							:class="{
								'border-red-600':
									validation.errors.turningSpeed,
								'border-grey-300':
									!validation.errors.turningSpeed,
							}"
							@input="validation.touch.turningSpeed()"
							@blur="validation.touch.turningSpeed()"
						/>
						<span class="ml-1 text-sm text-grey-600">mph</span>
					</div>
					<div
						v-if="validation.errors.turningSpeed"
						class="text-xs text-red-600 mt-1"
					>
						{{ validation.errors.turningSpeed }}
					</div>
				</div>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<span class="text-sm text-grey-750 uppercase font-semibold">
					Approach Speed
				</span>
				<div class="flex flex-col">
					<div class="flex items-center">
						<input
							v-model.number="formData.approachSpeed"
							type="number"
							min="0"
							step="0.1"
							class="w-20 px-2 py-1 border rounded text-center"
							:class="{
								'border-red-600':
									validation.errors.approachSpeed,
								'border-grey-300':
									!validation.errors.approachSpeed,
							}"
							@input="validation.touch.approachSpeed()"
							@blur="validation.touch.approachSpeed()"
						/>
						<span class="ml-1 text-sm text-grey-600">mph</span>
					</div>
					<div
						v-if="validation.errors.approachSpeed"
						class="text-xs text-red-600 mt-1"
					>
						{{ validation.errors.approachSpeed }}
					</div>
				</div>

				<div class="bg-grey-200 w-full h-px col-span-3"></div>

				<!-- Start Location Selection Button -->
				<span
					class="text-sm text-grey-750 uppercase font-semibold text-left"
				>
					Start Location
				</span>
				<button
					class="rounded-lg flex items-center p-2 font-medium"
					:class="{
						'bg-green-600 border-2 border-green-600 text-white':
							!pathPlanningOptions.startLocation,
						'text-orange-800 hover:bg-orange-50 border transition-colors':
							pathPlanningOptions.startLocation,
					}"
					@click.prevent="startLocationSelection"
				>
					<MapIcon class="w-6 h-6 mr-2" />
					<div>
						{{
							pathPlanningOptions.startLocation
								? "Unset Location"
								: "Select Location"
						}}
					</div>
				</button>
			</div>

			<div class="flex items-center mt-2 px-6 pb-6">
				<ButtonPrimary
					@click="handlePathPlanning"
					:disabled="!canPlanPath || isPlanning"
					class="flex-1 px-4 py-3 font-medium"
				>
					{{ isPlanning ? "Planning..." : "Generate Path" }}
				</ButtonPrimary>
			</div>
		</template>

		<template v-if="hasError && !isPlanning">
			<div class="p-6 text-center">
				<div class="mb-4">
					<div
						class="w-16 h-16 mx-auto text-red-600 mb-2"
						v-html="warningTriangleSvg"
					></div>
					<h3 class="text-lg font-semibold text-grey-900 mb-2">
						Path Planning Failed
					</h3>
					<p class="text-grey-600 mb-6">
						We couldn't generate a path for this field with the
						current settings. Try adjusting your planning options or
						field selection.
					</p>
				</div>
				<ButtonPrimary
					@click="hasError = false"
					class="px-6 py-3 font-medium"
				>
					Modify Options
				</ButtonPrimary>
			</div>
		</template>

		<div
			class="p-4 flex gap-4"
			v-else-if="isPlanning"
		>
			<div class="w-full loading-pattern rounded-xl h-10 overflow-hidden">
				<div
					class="bg-green-600 h-full transition-all duration-300 ease-out"
					:style="{
						width: `${progressPercentage}%`,
					}"
				></div>
			</div>
			<ButtonSecondary
				class="px-4 py-2 text-sm font-medium justify-self-end"
				:disabled="planningProgress === 0"
				@click="skipPlanning"
			>
				Skip
			</ButtonSecondary>
		</div>
	</template>

	<div v-if="isSelectingAbLine">
		<div
			class="flex items-center justify-between p-4 bg-green-50 border-b border-green-200"
		>
			<h3 class="title text-green-950 text-lg font-semibold">
				Select Alignment
			</h3>
			<button
				@click="cancelAbLineSelection"
				class="text-grey-400 hover:text-grey-600"
			>
				<CloseButtonAlt class="w-5 h-5" />
			</button>
		</div>
		<div class="p-4 space-y-4">
			<template v-if="selectedSegment">
				<div class="text-center">
					<span class="text-4xl font-bold text-green-900"
						>{{ selectedSegment.heading }}°</span
					>
					<p class="text-sm text-grey-500 mt-1">
						{{ Math.round(selectedSegment.length) }} m
					</p>
				</div>
				<div class="flex gap-3">
					<ButtonBack
						@click="cancelAbLineSelection"
						class="flex-1 px-4 py-3 font-medium"
					>
						Cancel
					</ButtonBack>
					<ButtonPrimary
						@click="confirmAbLineSelection"
						class="flex-1 px-4 py-3 font-medium"
					>
						Use {{ selectedSegment.heading }}°
					</ButtonPrimary>
				</div>
			</template>
			<template v-else>
				<p class="text-center text-grey-500 text-sm">
					Click a highlighted segment on the map.
				</p>
				<ButtonBack
					@click="cancelAbLineSelection"
					class="w-full px-4 py-3 font-medium"
				>
					Cancel
				</ButtonBack>
			</template>
		</div>
	</div>
	<div v-if="isSelectingStartLocation">
		<div
			class="flex items-center justify-between p-4 bg-green-50 border-b border-green-200"
		>
			<div class="flex items-center gap-3">
				<button
					@click="cancelStartLocationSelection"
					class="text-grey-400 hover:text-grey-600 p-1"
				>
					<ChevronLeftIcon class="w-5 h-5" />
				</button>
				<div>
					<h3 class="title text-green-800 text-lg font-semibold">
						Select Start Location
					</h3>
				</div>
			</div>
			<button
				@click="emits('resetOmnibar')"
				class="text-grey-400 hover:text-grey-600"
			>
				<CloseButtonAlt class="w-5 h-5" />
			</button>
		</div>

		<div class="p-4 space-y-4">
			<div class="text-center text-grey-600">
				<p class="mb-2">
					Drag the blue marker to select the starting location for
					your path.
				</p>
				<p class="text-sm text-grey-500">
					The marker must be placed within the field boundary to be
					valid.
				</p>
			</div>

			<div class="flex gap-3">
				<ButtonBack
					@click="cancelStartLocationSelection"
					class="flex-1 px-4 py-3 font-medium"
				>
					Cancel
				</ButtonBack>
				<ButtonPrimary
					@click="selectStartLocation"
					:disabled="!startLocationMarker || !isStartingLocationValid"
					class="flex-1 px-4 py-3 font-medium disabled:bg-grey-300 disabled:text-grey-700"
				>
					{{
						isStartingLocationValid
							? "Select Location"
							: "Invalid Location"
					}}
				</ButtonPrimary>
			</div>
		</div>
	</div>

	<MapLibreMarker
		v-if="
			startLocationMarker &&
			isSelectingStartLocation &&
			isStartingLocationValid
		"
		:marker="startLocationMarker"
		@dragend="updateStartLocation"
	/>

	<MapLibreMarker
		v-if="
			startLocationMarker &&
			isSelectingStartLocation &&
			!isStartingLocationValid
		"
		:marker="{
			location: startLocationMarker.location,
			icon: startPointerInvalidSvg,
			draggable: true,
		}"
		@dragend="updateStartLocation"
	/>

	<template v-if="isSelectingAbLine">
		<!-- Unselected segment outer lines -->
		<MapLibrePolyline
			v-for="(seg, i) in unselectedStraightSegments"
			:key="`seg-outline-${i}`"
			:polyline="lineString([seg.startPoint, seg.endPoint]).geometry"
			:stroke="abLineUnselectedOuterStroke"
			:z-index="8"
		/>
		<!-- Unselected segment inner lines -->
		<MapLibrePolyline
			v-for="(seg, i) in unselectedStraightSegments"
			:key="`seg-inner-${i}`"
			:polyline="lineString([seg.startPoint, seg.endPoint]).geometry"
			:stroke="abLineUnselectedInnerStroke"
			:z-index="9"
		/>
		<!-- Selected segment double line -->
		<MapLibrePolyline
			v-if="selectedSegment"
			key="selected-seg-outline"
			:polyline="
				lineString([
					selectedSegment.startPoint,
					selectedSegment.endPoint,
				]).geometry
			"
			:stroke="abLineSelectedOuterStroke"
			:z-index="8"
		/>
		<MapLibrePolyline
			v-if="selectedSegment"
			key="selected-seg-inner"
			:polyline="
				lineString([
					selectedSegment.startPoint,
					selectedSegment.endPoint,
				]).geometry
			"
			:stroke="abLineSelectedInnerStroke"
			:z-index="9"
		/>
		<!-- Segment angle labels -->
		<MapLibreLabel
			v-for="(seg, i) in unselectedStraightSegments"
			:key="`seg-label-unselected-${i}`"
			:location="segmentLabelLocation(seg)"
		>
			<div
				class="px-2 py-1 rounded-md text-xs font-semibold shadow border pointer-events-none bg-white text-blue-800 border-grey-200"
			>
				{{ seg.heading }}°
			</div>
		</MapLibreLabel>
		<MapLibreLabel
			v-if="selectedSegment"
			key="seg-label-selected"
			:location="segmentLabelLocation(selectedSegment)"
		>
			<div
				class="px-2 py-1 rounded-md text-xs font-semibold shadow border pointer-events-none bg-green-600 text-white border-green-800"
			>
				{{ selectedSegment.heading }}°
			</div>
		</MapLibreLabel>
	</template>
</template>

<style scoped>
	.loading-pattern {
		background: repeating-linear-gradient(
			-45deg,
			#0c9f50,
			#0c9f50 10px,
			#c5edda 10px,
			#c5edda 20px
		);
		background-size: 28px 28px;
		animation: move-stripes 1s linear infinite;
	}

	@keyframes move-stripes {
		0% {
			background-position: 0 0;
		}
		100% {
			background-position: 28px 28px;
		}
	}
</style>
